// Tvarra behaviour layer for the tv-* sections: add to cart (through Horizon's cart drawer),
// product rails, tabs, the shop-by-need toggle, reels and card galleries.
import { CartLinesUpdateEvent } from 'https://cdn.shopify.com/storefront/standard-events.js';

const money = (cents) =>
  '₹' + Math.round(cents / 100).toLocaleString('en-IN');

/* ---------- add to cart ---------- */

// Adds a variant through the AJAX cart and announces it with the same standard event Horizon's own
// product form uses, so the cart drawer, cart count and cart items all refresh and the drawer opens.
async function addVariant(source, id, quantity = 1, properties = null) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(id), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [properties ? { id, quantity, properties } : { id, quantity }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');

    const cart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'tv-card', itemCount: quantity, sections: data.sections, didError: false },
    });
    return true;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

function initAtc(root) {
  const json = root.querySelector('[data-tv-variants]');
  if (!json || root.dataset.tvReady) return;
  root.dataset.tvReady = '1';
  const { options, variants, url } = JSON.parse(json.textContent);
  const card = root.closest('[data-tv-card]');
  const addBtn = root.querySelector('[data-tv-add]');
  const choices = root.querySelector('[data-tv-choices]');
  const msg = root.querySelector('[data-tv-msg]');
  const colourIndex = options.findIndex((o) => /^colou?r$/i.test(o));
  const state = { colour: null };
  const preorder = addBtn?.dataset.preorder === 'true';
  const purchasable = (v) => !!v && (preorder || v.available);

  const say = (text, isError) => {
    msg.textContent = text;
    msg.classList.toggle('is-error', !!isError);
    clearTimeout(msg._t);
    msg._t = setTimeout(() => (msg.textContent = ''), 3500);
  };

  const add = async (variant, button) => {
    if (!purchasable(variant)) return;
    button.disabled = true;
    root.classList.add('is-busy');
    try {
      await addVariant(button, variant.id);
      say('Added to cart');
      close();
    } catch (e) {
      say(e.message, true);
    } finally {
      button.disabled = false;
      root.classList.remove('is-busy');
    }
  };

  const close = () => {
    choices.hidden = true;
    choices.innerHTML = '';
    addBtn.hidden = false;
  };

  // Variants still possible given the colour picked on the card (if the product has a colour option).
  const pool = () =>
    colourIndex >= 0 && state.colour ? variants.filter((v) => v.options[colourIndex] === state.colour) : variants;

  addBtn?.addEventListener('click', () => {
    const list = pool();
    if (list.length === 1) return add(list[0], addBtn);
    // Choose on the first option that isn't colour; anything with 2+ such options goes to the product page.
    const pickIndexes = options.map((_, i) => i).filter((i) => i !== colourIndex);
    if (pickIndexes.length !== 1) return (window.location.href = url);
    const pick = pickIndexes[0];
    choices.innerHTML = '';
    list.forEach((v) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.disabled = !purchasable(v);
      b.innerHTML = `${v.options[pick]}${purchasable(v) ? '' : '<small>Sold out</small>'}`;
      b.addEventListener('click', () => add(v, b));
      choices.append(b);
    });
    const x = document.createElement('button');
    x.type = 'button';
    x.className = 'tc__x';
    x.setAttribute('aria-label', 'Cancel');
    x.innerHTML = '&times;';
    x.addEventListener('click', close);
    choices.append(x);
    choices.setAttribute('role', 'group');
    choices.setAttribute('aria-label', `Pick ${options[pick]}`);
    addBtn.hidden = true;
    choices.hidden = false;
    choices.querySelector('button:not(:disabled)')?.focus();
  });

  // Colour dots on products that carry colour as a variant option.
  card?.querySelectorAll('[data-tv-colour]').forEach((dot) => {
    dot.addEventListener('click', () => {
      state.colour = dot.dataset.tvColour;
      card.querySelectorAll('[data-tv-colour]').forEach((d) => {
        d.classList.toggle('on', d === dot);
        d.setAttribute('aria-checked', d === dot ? 'true' : 'false');
      });
      const v = pool().find((x) => x.available) || pool()[0];
      if (!v) return;
      const img = card.querySelector('[data-tv-track] img');
      if (v.image && img) {
        img.removeAttribute('srcset');
        img.src = v.image;
      }
      const price = card.querySelector('.tc__price');
      if (price) price.innerHTML = `${v.compare > v.price ? `<s>${money(v.compare)}</s>` : ''}<b>${money(v.price)}</b>`;
      const sale = card.querySelector('.tc__sale b');
      if (sale) {
        sale.textContent = money(v.price);
        const mrp = card.querySelector('.tc__mrp s');
        if (mrp && v.compare > v.price) mrp.textContent = `MRP ${money(v.compare)}`;
        const saveEl = card.querySelector('.tc__save');
        if (saveEl && v.compare > v.price) saveEl.textContent = `Save ${money(v.compare - v.price)}`;
        const pill = card.querySelector('.tc__dpill');
        if (pill && v.compare > v.price) pill.textContent = `${Math.round(((v.compare - v.price) * 100) / v.compare)}% OFF`;
      }
      const anyAvailable = pool().some(purchasable);
      addBtn.disabled = !anyAvailable;
      addBtn.firstChild.textContent = preorder ? 'Pre-order now ' : anyAvailable ? 'Add to cart ' : 'Sold out';
      close();
    });
  });
}

/* ---------- card gallery (swipe + hover + dots) ---------- */

function initGallery(card) {
  const track = card.querySelector('[data-tv-track]');
  if (!track || track.dataset.tvReady) return;
  track.dataset.tvReady = '1';
  const dots = [...card.querySelectorAll('.tc__dots i')];
  const go = (n) => track.scrollTo({ left: n * track.clientWidth, behavior: 'smooth' });
  track.addEventListener(
    'scroll',
    () => {
      const n = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      dots.forEach((d, i) => d.classList.toggle('on', i === n));
    },
    { passive: true }
  );
  if (track.children.length > 1 && matchMedia('(hover: hover)').matches) {
    const media = card.querySelector('.tc__media');
    media.addEventListener('mouseenter', () => go(1));
    media.addEventListener('mouseleave', () => go(0));
  }
}

/* ---------- rails ---------- */

function initRail(rail) {
  if (rail.dataset.tvReady) return;
  rail.dataset.tvReady = '1';
  const track = rail.querySelector('.rail__track');
  const ctrl = rail.querySelector('.rail__ctrl');
  if (!track || !ctrl) return;
  const [prev, next] = ctrl.querySelectorAll('.round');
  const dotsWrap = ctrl.querySelector('.dots');
  const measure = () => {
    const pages = Math.max(1, Math.round(track.scrollWidth / Math.max(1, track.clientWidth)));
    const max = Math.max(1, track.scrollWidth - track.clientWidth);
    const page = Math.round((track.scrollLeft / max) * (pages - 1)) || 0;
    ctrl.hidden = pages < 2;
    const shown = Math.min(pages, 6);
    if (dotsWrap.children.length !== shown) dotsWrap.innerHTML = '<span></span>'.repeat(shown);
    [...dotsWrap.children].forEach((d, i) => d.classList.toggle('on', i === Math.min(page, 5)));
    prev.disabled = track.scrollLeft < 4;
    next.disabled = track.scrollLeft > max - 4;
  };
  prev.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth * 0.9, behavior: 'smooth' }));
  next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth * 0.9, behavior: 'smooth' }));
  track.addEventListener('scroll', measure, { passive: true });
  new ResizeObserver(measure).observe(track);
  measure();
}

/* ---------- tabs (product shelf) + toggle (shop by need) ---------- */

function initTabs(root) {
  if (root.dataset.tvReady) return;
  root.dataset.tvReady = '1';
  const tabs = [...root.querySelectorAll('[data-tv-tab]')];
  const panels = [...root.querySelectorAll('[data-tv-panel]')];
  const toggle = root.querySelector('.toggle');
  tabs.forEach((tab, i) =>
    tab.addEventListener('click', () => {
      tabs.forEach((t) => {
        t.classList.toggle('on', t === tab);
        t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
      });
      panels.forEach((p, k) => (p.hidden = k !== i));
      if (toggle) toggle.style.setProperty('--i', i);
      panels[i]?.querySelectorAll('.rail').forEach((r) => r.querySelector('.rail__track')?.dispatchEvent(new Event('scroll')));
    })
  );
}

/* ---------- reels ---------- */

function initReels(root) {
  if (root.dataset.tvReady) return;
  root.dataset.tvReady = '1';
  const slides = [...root.querySelectorAll('.sv__slide')];
  const dots = [...root.querySelectorAll('.sv__dots button')];
  const n = slides.length;
  if (!n) return;
  let i = 0;
  let muted = true;

  const render = () => {
    slides.forEach((slide, k) => {
      let d = k - i;
      if (d > n / 2) d -= n;
      if (d < -n / 2) d += n;
      const active = d === 0;
      slide.style.setProperty('--d', d);
      slide.style.setProperty('--ad', Math.abs(d));
      slide.hidden = Math.abs(d) > 3;
      slide.classList.toggle('is-active', active);
      const v = slide.querySelector('video');
      if (!v) return;
      v.muted = !active || muted;
      if (active) {
        v.preload = 'auto';
        v.play().catch(() => {
          v.muted = true;
          v.play().catch(() => {});
        });
      } else v.pause();
      const m = slide.querySelector('.sv__mute');
      if (m) m.hidden = !active;
    });
    dots.forEach((d, k) => d.classList.toggle('on', k === i));
  };
  const go = (k) => {
    i = (k + n) % n;
    render();
  };

  slides.forEach((s, k) => s.addEventListener('click', (e) => {
    if (!s.classList.contains('is-active') && !e.target.closest('a, button')) go(k);
  }));
  dots.forEach((d, k) => d.addEventListener('click', () => go(k)));
  root.querySelector('.sv__arrow--l')?.addEventListener('click', () => go(i - 1));
  root.querySelector('.sv__arrow--r')?.addEventListener('click', () => go(i + 1));
  root.querySelectorAll('.sv__mute').forEach((m) =>
    m.addEventListener('click', (e) => {
      e.stopPropagation();
      muted = !muted;
      root.classList.toggle('is-unmuted', !muted);
      render();
    })
  );
  const stage = root.querySelector('.sv__stage');
  let startX = null;
  stage.addEventListener('pointerdown', (e) => (startX = e.clientX));
  stage.addEventListener('pointerup', (e) => {
    if (startX == null) return;
    const dx = e.clientX - startX;
    if (Math.abs(dx) > 40) go(i + (dx < 0 ? 1 : -1));
    startX = null;
  });

  // Only play while the section is on screen.
  new IntersectionObserver(([entry]) => {
    const v = slides[i]?.querySelector('video');
    if (!v) return;
    if (entry.isIntersecting) render();
    else v.pause();
  }).observe(root);
}

/* ---------- product recommendations ---------- */

async function initRelated(el) {
  if (el.dataset.tvReady || el.querySelector('.rail')) return;
  el.dataset.tvReady = '1';
  try {
    const html = await fetch(el.dataset.url).then((r) => r.text());
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-tv-related]');
    if (!fresh || !fresh.querySelector('.rail')) return;
    el.innerHTML = fresh.innerHTML;
    boot(el);
  } catch (e) {
    /* recommendations are optional */
  }
}

/* ---------- product page ---------- */

function formatMoney(cents, format) {
  const rupees = Math.round(cents / 100).toLocaleString('en-IN');
  const exact = (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (format || '₹{{amount_no_decimals}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => (k === 'amount' ? exact.replace(/\.00$/, '') : rupees));
}

function initPdp(root) {
  if (root.dataset.tvReady) return;
  root.dataset.tvReady = '1';
  const product = JSON.parse(root.querySelector('[data-tv-product]').textContent);
  const fmt = (c) => formatMoney(c, root.dataset.moneyFormat);
  const $ = (sel) => root.querySelector(sel);
  const $$ = (sel) => [...root.querySelectorAll(sel)];

  /* gallery */
  const track = $('.pg__track');
  const thumbs = $$('.pg__thumbs button');
  const dots = $$('.pg__dots span');
  const count = $('[data-tv-count]');
  let index = 0;
  const slides = () => track ? track.children.length : 0;
  const go = (n) => {
    if (!track || !slides()) return;
    const k = (n + slides()) % slides();
    track.scrollTo({ left: k * track.clientWidth, behavior: 'smooth' });
  };
  track?.addEventListener('scroll', () => {
    index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    thumbs.forEach((t, i) => t.classList.toggle('on', i === index));
    dots.forEach((d, i) => d.classList.toggle('on', i === index));
    if (count) count.textContent = index + 1;
    thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, { passive: true });
  thumbs.forEach((t, i) => t.addEventListener('click', () => go(i)));
  $('[data-tv-prev]')?.addEventListener('click', () => go(index - 1));
  $('[data-tv-next]')?.addEventListener('click', () => go(index + 1));
  $('[data-tv-share]')?.addEventListener('click', async (e) => {
    const url = window.location.href;
    if (navigator.share) return navigator.share({ title: product.title, url }).catch(() => {});
    await navigator.clipboard?.writeText(url);
    e.currentTarget.classList.add('is-done');
    setTimeout(() => e.currentTarget?.classList.remove('is-done'), 1600);
  });
  const showMedia = (mediaId) => {
    const i = thumbs.findIndex((t) => t.dataset.mediaId === String(mediaId));
    if (i >= 0) go(i);
  };

  /* variants */
  const params = new URLSearchParams(window.location.search);
  let variant =
    product.variants.find((v) => String(v.id) === params.get('variant')) ||
    product.variants.find((v) => v.available) ||
    product.variants[0];
  const opts = [...variant.options];
  const groups = $$('[data-tv-option]');
  const addBtn = $('[data-tv-pdp-add]');
  const sbarBtn = $('[data-tv-sbar-add]');
  const msg = $('[data-tv-pdp-msg]');

  const priceHtml = (v) => {
    const pct = v.compare_at_price > v.price ? Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price) : 0;
    return `<b>${fmt(v.price)}</b>${pct ? `<s>MRP ${fmt(v.compare_at_price)}</s><span class="pill-off">${pct}% OFF</span>` : ''}<small>(Incl. of all taxes)</small>`;
  };

  const render = (fromUser) => {
    const match = product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    if (match) variant = match;
    groups.forEach((g) => {
      const i = Number(g.dataset.tvOption);
      g.querySelector('[data-tv-option-value]').textContent = opts[i];
      g.querySelectorAll('[data-value]').forEach((b) => {
        const value = b.dataset.value;
        const on = value === opts[i];
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        // available if some variant with this value and the other current choices is in stock
        const ok = product.variants.some((v) => v.options[i] === value && v.available && v.options.every((o, k) => k === i || o === opts[k]));
        const exists = product.variants.some((v) => v.options[i] === value && v.options.every((o, k) => k === i || o === opts[k]));
        b.classList.toggle('is-out', !ok);
        const out = b.querySelector('.bb__out');
        if (out) out.hidden = ok || !exists;
      });
    });
    const preorder = addBtn?.dataset.preorder === 'true';
    const available = !!match && (preorder || variant.available);
    const label = !match ? 'Unavailable' : available ? 'Add to Cart' : 'Sold out';
    if (addBtn) {
      addBtn.disabled = !available;
      addBtn.innerHTML = preorder ? 'Pre-order now' : available ? `Add to Cart ${addBtn.dataset.icon || ''}` : label;
    }
    if (sbarBtn) {
      sbarBtn.disabled = !available;
      const size = groups.length ? ` · ${opts[Number(groups[groups.length - 1].dataset.tvOption)]}` : '';
      sbarBtn.textContent = preorder ? 'Pre-order now' : available ? `Add to Cart${size}` : label;
    }
    const p = $('[data-tv-price]');
    if (p && match) p.innerHTML = priceHtml(variant);
    const sp = $('[data-tv-sbar-price]');
    if (sp && match) sp.innerHTML = priceHtml(variant);
    const cp = $('[data-tv-combo-price]');
    if (cp && match) cp.textContent = fmt(variant.price);
    const sku = $('[data-tv-sku]');
    if (sku && match) sku.textContent = variant.sku || '—';
    if (fromUser && match) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      if (variant.featured_media) showMedia(variant.featured_media.id);
    }
    offers.refresh();
  };

  groups.forEach((g) => {
    const i = Number(g.dataset.tvOption);
    g.querySelectorAll('[data-value]').forEach((b) =>
      b.addEventListener('click', () => {
        opts[i] = b.dataset.value;
        // if that combination doesn't exist, jump to the first available variant with the picked value
        if (!product.variants.some((v) => v.options.every((o, k) => o === opts[k]))) {
          const alt = product.variants.find((v) => v.options[i] === opts[i] && v.available) || product.variants.find((v) => v.options[i] === opts[i]);
          if (alt) alt.options.forEach((o, k) => (opts[k] = o));
        }
        render(true);
      })
    );
  });

  /* rotating offers in the price box */
  const offers = (() => {
    const btn = $('[data-tv-offers]');
    const text = $('[data-tv-offer-text]');
    const tpl = $('[data-tv-offer-lines]');
    const lines = tpl ? [...tpl.content.children] : [];
    let i = 0;
    let paused = false;
    const show = () => {
      if (!lines.length) return;
      const node = lines[i].cloneNode(true);
      node.querySelectorAll('[data-calc]').forEach((b) => {
        const kind = b.dataset.calc;
        const price = variant.price;
        if (kind === 'sale') b.textContent = fmt(Math.round((price * (100 - Number(root.dataset.salePct || 0))) / 100));
        if (kind === 'upi') b.textContent = fmt(price - Number(root.dataset.upiOff || 0) * 100);
        if (kind === 'save') b.textContent = fmt(Math.max(0, (variant.compare_at_price || 0) - price));
        if (kind === 'emi') b.textContent = `${fmt(Math.ceil(price / 300) * 100)} × 3`;
      });
      node.querySelectorAll('[data-tv-dealclock]').forEach((c) => { c.textContent = dealHms(); });
      text.replaceChildren(...node.childNodes);
      text.style.animation = 'none';
      void text.offsetWidth;
      text.style.animation = '';
    };
    if (!lines.length && btn) btn.hidden = true;
    btn?.addEventListener('mouseenter', () => (paused = true));
    btn?.addEventListener('mouseleave', () => (paused = false));
    btn?.addEventListener('click', () => { i = (i + 1) % lines.length; show(); });
    if (lines.length > 1) setInterval(() => { if (!paused && !document.hidden) { i = (i + 1) % lines.length; show(); } }, 3200);
    return { refresh: show };
  })();

  /* sale countdown to midnight */
  const cd = $('[data-tv-countdown]');
  if (cd) {
    const tick = () => {
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      const s = Math.max(0, Math.floor((end - Date.now()) / 1000));
      cd.textContent = [s / 3600, (s % 3600) / 60, s % 60].map((n) => String(Math.floor(n)).padStart(2, '0')).join(':');
    };
    tick();
    setInterval(tick, 1000);
  }

  /* combos */
  const comboLink = $('[data-tv-combo-link]');
  const comboLabel = $('[data-tv-combo-label]');
  $$('[data-tv-combo]').forEach((r) =>
    r.addEventListener('change', () => {
      $$('.of').forEach((l) => l.classList.toggle('on', l.contains(r) && r.checked));
      const single = r.value === 'single';
      addBtn.hidden = !single;
      comboLink.hidden = single;
      if (!single) {
        comboLink.href = r.dataset.href || '#';
        comboLink.innerHTML = `Build your ${r.dataset.title} ${addBtn.dataset.arrow || '→'}`;
      }
      if (comboLabel) comboLabel.textContent = single ? '1 item' : r.dataset.save || r.dataset.title;
    })
  );

  /* HYP3: measure-first size helper + required size confirmation */
  const kfit = $('[data-tv-kfit]');
  const kfitOk = kfit?.querySelector('[data-kfit-ok]');
  const kfitCm = kfit?.querySelector('[data-kfit-cm]');
  const kfitRes = kfit?.querySelector('[data-kfit-result]');
  const pickSize = (size) => {
    const btn = kfit?.closest('.bb__group')?.querySelector(`.bb__sizes [data-value="${size}"]`);
    if (btn && !btn.classList.contains('on')) btn.click();
    kfit?.querySelectorAll('[data-kfit-size]').forEach((c) => c.classList.toggle('on', c.dataset.kfitSize === size));
  };
  if (kfit) {
    kfit.querySelectorAll('[data-kfit-size]').forEach((c) => c.addEventListener('click', () => pickSize(c.dataset.kfitSize)));
    kfitCm.addEventListener('input', () => {
      const cm = parseFloat(kfitCm.value);
      kfit.classList.remove('is-warn');
      if (!cm) { kfitRes.textContent = ''; return; }
      let size = '', note = '';
      if (cm < 46 || cm > 58) note = 'That looks unusual for a head size. Please measure again in cm.';
      else if (cm < 48) note = 'Smaller than our XS. Message us on WhatsApp and we\'ll help you choose.';
      else if (cm > 54) note = 'Larger than our M. Message us on WhatsApp and we\'ll help you choose.';
      else if (cm < 50) size = 'XS';
      else if (cm < 52) size = 'S';
      else size = 'M';
      if (size) {
        const edge = cm === 50 || cm === 52;
        pickSize(size);
        kfitRes.innerHTML = `Recommended size: <b>${size}</b>${edge ? ' (on the line between two sizes, so we picked the bigger one)' : ''}. Selected for you.`;
        kfitOk.checked = true;
        kfit.classList.remove('needs-ok');
      } else {
        kfitRes.textContent = note;
        kfit.classList.add('is-warn');
      }
    });
    kfitOk.addEventListener('change', () => kfit.classList.toggle('needs-ok', !kfitOk.checked && kfit.classList.contains('needs-ok')));
  }

  /* add to cart */
  const add = async (button) => {
    if (!variant?.available) return;
    if (kfit && !kfitOk.checked) {
      kfit.classList.add('needs-ok');
      kfit.scrollIntoView({ behavior: 'smooth', block: 'center' });
      msg.textContent = "Please check your child's head size first.";
      msg.classList.add('is-error');
      setTimeout(() => (msg.textContent = ''), 3500);
      return;
    }
    button.disabled = true;
    try {
      const cm = kfitCm && parseFloat(kfitCm.value);
      await addVariant(button, variant.id, 1, cm ? { 'Child head size': `${cm} cm` } : null);
      msg.textContent = 'Added to cart';
      msg.classList.remove('is-error');
    } catch (e) {
      msg.textContent = e.message;
      msg.classList.add('is-error');
    } finally {
      button.disabled = !variant.available;
      setTimeout(() => (msg.textContent = ''), 3500);
    }
  };
  if (addBtn) addBtn.dataset.icon = addBtn.querySelector('svg')?.outerHTML || '';
  addBtn?.addEventListener('click', () => add(addBtn));
  sbarBtn?.addEventListener('click', () => add(sbarBtn));

  /* size guide */
  const guide = $('[data-tv-guide]');
  const guideVideo = guide?.querySelector('[data-tv-guide-video]');
  const openGuide = (e) => {
    if (!guide) return;
    guide.classList.add('is-open');
    guide.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    guide.querySelector('[data-tv-guide-close].icon-btn')?.focus();
    if (guideVideo && e?.currentTarget?.hasAttribute('data-tv-guide-play')) guideVideo.play().catch(() => {});
  };
  const closeGuide = () => {
    if (!guide) return;
    guideVideo?.pause();
    guide.classList.remove('is-open');
    guide.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  };
  $$('[data-tv-guide-open]').forEach((b) => b.addEventListener('click', openGuide));
  $$('[data-tv-guide-close]').forEach((b) => b.addEventListener('click', closeGuide));
  document.addEventListener('keydown', (e) => e.key === 'Escape' && guide?.classList.contains('is-open') && closeGuide());

  /* sticky bar once the main button has scrolled away */
  const buy = $('[data-tv-buy]');
  const sbar = $('[data-tv-sbar]');
  if (buy && sbar) {
    new IntersectionObserver(([entry]) => {
      // phones: keep "Add to cart" in reach whenever the main button is off screen (above or below);
      // larger screens: only after scrolling past it
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const show = !entry.isIntersecting && (mobile || entry.boundingClientRect.top < 0);
      sbar.classList.toggle('is-on', show);
      sbar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (sbarBtn) sbarBtn.tabIndex = show ? 0 : -1;
      document.documentElement.classList.toggle('tv-sbar-on', show);
    }).observe(buy);
  }

  render(false);
}

/* ---------- collection: design pills + quick filters ---------- */

function initCollection(root) {
  if (root.dataset.tvReady) return;
  root.dataset.tvReady = '1';
  const grid = root.querySelector('[data-tv-grid]');
  if (!grid) return;
  const items = [...grid.querySelectorAll('.tcol__item')];
  const band = grid.querySelector('[data-tv-band]');
  const designs = [...root.querySelectorAll('[data-tv-design]')];
  const quick = [...root.querySelectorAll('[data-tv-quick]')];
  const clears = [...root.querySelectorAll('[data-tv-clear]')];
  const count = root.querySelector('[data-tv-count]');
  const empty = root.querySelector('[data-tv-empty]');
  const state = { design: '', flags: new Set() };

  const apply = () => {
    let shown = 0;
    items.forEach((item) => {
      const flags = (item.dataset.flags || '').split(' ');
      const ok = (!state.design || item.dataset.design === state.design) && [...state.flags].every((f) => flags.includes(f));
      item.hidden = !ok;
      if (ok) shown += 1;
    });
    // keep the promo band after the Nth visible product (or at the end)
    if (band) {
      const visible = items.filter((i) => !i.hidden);
      const pos = Number(band.dataset.position || 8);
      const before = visible[pos];
      if (before) grid.insertBefore(band, before);
      else grid.append(band);
      band.hidden = shown === 0;
    }
    if (count) count.textContent = `${shown} ${shown === 1 ? count.dataset.noun : count.dataset.nouns}`;
    const filtering = !!state.design || state.flags.size > 0;
    clears.forEach((c) => (c.hidden = c.closest('[data-tv-empty]') ? false : !filtering));
    if (empty) empty.hidden = shown > 0;
    grid.hidden = shown === 0;
  };

  designs.forEach((b) =>
    b.addEventListener('click', () => {
      state.design = b.dataset.tvDesign;
      designs.forEach((d) => {
        d.classList.toggle('on', d === b);
        d.setAttribute('aria-pressed', d === b ? 'true' : 'false');
      });
      apply();
    })
  );
  quick.forEach((b) =>
    b.addEventListener('click', () => {
      const f = b.dataset.tvQuick;
      if (state.flags.has(f)) state.flags.delete(f);
      else state.flags.add(f);
      const on = state.flags.has(f);
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      apply();
    })
  );
  clears.forEach((c) =>
    c.addEventListener('click', () => {
      state.design = '';
      state.flags.clear();
      designs.forEach((d, i) => {
        d.classList.toggle('on', i === 0);
        d.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
      });
      quick.forEach((q) => {
        q.classList.remove('on');
        q.setAttribute('aria-pressed', 'false');
      });
      apply();
    })
  );
  apply();
}

/* ---------- product reviews (Judge.me data, Tvarra layout) ---------- */

const starsHtml = (value, size) =>
  `<span class="stars" style="--fill:${Math.min(100, (value / 5) * 100)}%;font-size:${size}px" role="img" aria-label="${value.toFixed(1)} out of 5"><span aria-hidden="true">★★★★★</span><span class="stars__on" aria-hidden="true">★★★★★</span></span>`;

async function fetchJudgeme(shop, productId) {
  const url = `https://cdn.judge.me/reviews/reviews_for_widget?url=${shop}&shop_domain=${shop}&platform=shopify&page=1&per_page=100&product_id=${productId}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('reviews unavailable');
  const data = await res.json();
  const doc = new DOMParser().parseFromString(data.html || '', 'text/html');
  return [...doc.querySelectorAll('.jdgm-rev')].map((r) => ({
    rating: Number(r.querySelector('.jdgm-rev__rating')?.dataset.score || 0),
    title: r.querySelector('.jdgm-rev__title')?.textContent.trim() || '',
    body: r.querySelector('.jdgm-rev__body')?.textContent.trim() || '',
    name: r.querySelector('.jdgm-rev__author')?.textContent.trim() || 'Anonymous',
    verified: r.dataset.verifiedBuyer === 'true',
    date: r.querySelector('.jdgm-rev__timestamp')?.getAttribute('datetime') || '',
    pics: [...r.querySelectorAll('.jdgm-rev__pic-link')].map((a) => ({
      full: a.getAttribute('href'),
      thumb: (a.querySelector('img')?.dataset.src || a.getAttribute('href') || '').replace(/width=\d+/, 'width=500'),
    })),
  }));
}

function initReviews(root) {
  if (root.dataset.tvReady) return;
  root.dataset.tvReady = '1';
  const shop = root.dataset.shop;
  const per = Number(root.dataset.per || 15);
  const siblings = (root.dataset.siblings || '').split('||').filter(Boolean).map((x) => {
    const [id, name] = x.split('::');
    return { id, name };
  }).slice(0, 8);
  const curated = (root.dataset.curated || '').split('|').map((x) => x.trim()).filter((x) => /^https?:\/\//.test(x));
  const $ = (sel) => root.querySelector(sel);
  const wall = $('[data-tv-rs-wall]');
  const strip = $('[data-tv-rs-strip]');
  const more = $('[data-tv-rs-more]');
  const jm = $('[data-tv-rs-jm]');
  let list = [];
  let shown = 0;

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return isNaN(d) ? '' : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const card = (r) => {
    const el = document.createElement('article');
    el.className = 'rc';
    if (r.pics[0]) {
      const img = document.createElement('img');
      img.className = 'rc__pic';
      img.loading = 'lazy';
      img.alt = '';
      img.src = r.pics[0].thumb;
      el.append(img);
    }
    const body = document.createElement('div');
    body.className = 'rc__body';
    body.insertAdjacentHTML('beforeend', starsHtml(r.rating, 15));
    if (r.title && !r.body.toLowerCase().startsWith(r.title.toLowerCase().replace(/[.!]+$/, ''))) {
      const b = document.createElement('b');
      b.className = 'rc__title';
      b.textContent = r.title;
      body.append(b);
    }
    const p = document.createElement('p');
    p.textContent = r.body;
    body.append(p);
    const n = document.createElement('p');
    n.className = 'rc__name';
    n.textContent = r.name;
    body.append(n);
    if (r.verified) body.insertAdjacentHTML('beforeend', '<p class="rc__ver"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg> verified buyer</p>');
    const d = document.createElement('p');
    d.className = 'rc__date';
    d.textContent = fmtDate(r.date) + (r.from ? ` · on ${r.from}` : '');
    body.append(d);
    el.append(body);
    return el;
  };

  const showMore = () => {
    const next = list.slice(shown, shown + per);
    next.forEach((r) => wall.append(card(r)));
    shown += next.length;
    more.hidden = shown >= list.length;
  };

  const summary = (reviews, family) => {
    const count = reviews.length;
    if (!count) return;
    const avg = reviews.reduce((s, r) => s + r.rating, 0) / count;
    const hist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    reviews.forEach((r) => (hist[Math.round(r.rating)] = (hist[Math.round(r.rating)] || 0) + 1));
    const max = Math.max(1, ...Object.values(hist));
    $('[data-tv-rs-avg]').textContent = avg.toFixed(1);
    $('[data-tv-rs-stars]').innerHTML = starsHtml(avg, 26);
    $('[data-tv-rs-count]').textContent = `Based on ${count} review${count === 1 ? '' : 's'}`;
    $('[data-tv-rs-label]').textContent = family ? 'Across this design' : 'Overall Rating';
    $('[data-tv-rs-note]').hidden = !family;
    root.querySelectorAll('[data-tv-rs-hist] li').forEach((li) => {
      const n = hist[li.dataset.star] || 0;
      li.querySelector('i > i').style.width = `${(n / max) * 100}%`;
      li.querySelector('em').textContent = n;
    });
  };

  const load = async () => {
    let own = [];
    try {
      own = await fetchJudgeme(shop, root.dataset.product);
    } catch (e) {
      // Judge.me unreachable: fall back to the Judge.me widget itself
      jm.hidden = false;
      root.querySelector('.rs').hidden = true;
      return;
    }
    let others = [];
    if (own.length < 6 && siblings.length) {
      const results = await Promise.allSettled(siblings.map((s) => fetchJudgeme(shop, s.id).then((rs) => rs.map((r) => ({ ...r, from: s.name })))));
      others = results.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
      others.sort((a, b) => (b.pics.length > 0) - (a.pics.length > 0) || b.rating - a.rating);
    }
    own.sort((a, b) => (b.pics.length > 0) - (a.pics.length > 0) || new Date(b.date) - new Date(a.date));
    list = [...own, ...others].filter((r) => r.body);
    summary(own.length ? own : others, !own.length && others.length > 0);

    const photos = [...list.flatMap((r) => r.pics), ...curated.map((u) => ({ full: u, thumb: u }))];
    strip.hidden = photos.length === 0;
    photos.slice(0, 30).forEach((ph) => {
      const a = document.createElement('a');
      a.href = ph.full;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.alt = 'Customer photo';
      img.src = ph.thumb.replace(/width=\d+/, 'width=240');
      a.append(img);
      strip.append(a);
    });
    if (!list.length) {
      wall.innerHTML = '<p class="rs__empty">No reviews yet. Be the first to review this product.</p>';
      root.querySelector('.rs').hidden = true;
    }
    showMore();
  };

  more.addEventListener('click', showMore);
  $('[data-tv-rs-write]').addEventListener('click', () => {
    jm.hidden = false;
    const btn = jm.querySelector('.jdgm-write-rev-link');
    if (btn) btn.click();
    jm.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  // load when the section comes near the viewport
  const io = new IntersectionObserver((entries) => {
    if (entries.some((e) => e.isIntersecting)) {
      io.disconnect();
      load();
    }
  }, { rootMargin: '600px' });
  io.observe(root);
}

/* ---------- offer strip ---------- */

function initStrip(root) {
  if (root.dataset.tvReady) return;
  root.dataset.tvReady = '1';
  const pills = [...root.querySelectorAll('[data-tv-strip-pill]')];
  const amount = root.querySelector('[data-tv-strip-amount]');
  const toggle = root.querySelector('[data-tv-strip-toggle]');
  const panel = root.querySelector('[data-tv-strip-panel]');
  const tabs = [...root.querySelectorAll('[data-tv-strip-tab]')];
  const bodies = tabs.map((t) => root.querySelector(`#${t.getAttribute('aria-controls')}`));
  let i = 0;

  const select = (k) => {
    tabs.forEach((t, n) => {
      t.classList.toggle('is-on', n === k);
      t.setAttribute('aria-selected', n === k ? 'true' : 'false');
      if (bodies[n]) bodies[n].hidden = n !== k;
    });
  };
  tabs.forEach((t, n) => t.addEventListener('click', () => select(n)));

  if (pills.length > 1) {
    setInterval(() => {
      if (document.hidden || root.classList.contains('is-open')) return;
      const prev = pills[i];
      i = (i + 1) % pills.length;
      prev.classList.remove('is-on');
      prev.classList.add('is-out');
      setTimeout(() => prev.classList.remove('is-out'), 450);
      pills[i].classList.add('is-on');
      if (amount) amount.textContent = pills[i].dataset.amount;
    }, 2800);
  }

  const open = (on) => {
    root.classList.toggle('is-open', on);
    toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
    panel.hidden = !on;
    if (on) select(i);
  };
  toggle.addEventListener('click', () => open(panel.hidden));
  document.addEventListener('click', (e) => !panel.hidden && !root.contains(e.target) && open(false));
  document.addEventListener('keydown', (e) => e.key === 'Escape' && !panel.hidden && open(false));
}

/* ---------- header: search, drawer, cart count ---------- */

function initHeader(header) {
  if (header.dataset.tvReady) return;
  header.dataset.tvReady = '1';
  const section = header.closest('.shopify-section') || document;

  // sticky offsets for the collection toolbar
  const measure = () => document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
  measure();
  new ResizeObserver(measure).observe(header);

  // search
  const search = header.querySelector('[data-tv-search]');
  const openBtn = header.querySelector('[data-tv-search-open]');
  const setSearch = (on) => {
    search.hidden = !on;
    openBtn.setAttribute('aria-expanded', on ? 'true' : 'false');
    if (on) search.querySelector('input[name="q"]').focus();
  };
  openBtn?.addEventListener('click', () => setSearch(search.hidden));
  header.querySelector('[data-tv-search-close]')?.addEventListener('click', () => setSearch(false));
  document.addEventListener('keydown', (e) => e.key === 'Escape' && search && !search.hidden && setSearch(false));

  // rotating placeholder ideas
  section.querySelectorAll('[data-tv-search-input]').forEach((input) => {
    const terms = (input.dataset.terms || '').split(',').map((t) => t.trim()).filter(Boolean);
    if (terms.length < 2) return;
    let k = 0;
    setInterval(() => {
      if (document.activeElement === input || input.value) return;
      k = (k + 1) % terms.length;
      input.placeholder = `Search for '${terms[k]}'`;
    }, 2500);
  });

  // drawer
  const drawer = section.querySelector('[data-tv-drawer]');
  const burger = header.querySelector('[data-tv-drawer-open]');
  const setDrawer = (on) => {
    if (!drawer) return;
    drawer.classList.toggle('is-open', on);
    drawer.setAttribute('aria-hidden', on ? 'false' : 'true');
    burger?.setAttribute('aria-expanded', on ? 'true' : 'false');
    document.documentElement.classList.toggle('tv-locked', on);
    if (on) drawer.querySelector('[data-tv-drawer-close].tvh__icon')?.focus();
    else burger?.focus();
  };
  burger?.addEventListener('click', () => setDrawer(true));
  drawer?.querySelectorAll('[data-tv-drawer-close]').forEach((b) => b.addEventListener('click', () => setDrawer(false)));
  document.addEventListener('keydown', (e) => e.key === 'Escape' && drawer?.classList.contains('is-open') && setDrawer(false));

  // cart button: Shopflo cart (handled in tv-tracking-body) or Horizon's drawer as a fallback
  header.querySelector('[data-tv-cart]')?.addEventListener('click', () => {
    if (typeof window.handleFloCartBtn === 'function') return;
    const drawerEl = document.getElementById('cart-drawer');
    if (drawerEl && typeof drawerEl.toggle === 'function') drawerEl.toggle();
    else window.location.href = '/cart';
  });

  // cart count: from cart events, and re-read when the shopper comes back or the Shopflo cart talks to the page
  const count = header.querySelector('[data-tv-cart-count]');
  const show = (n) => {
    if (!count) return;
    count.textContent = n;
    count.hidden = !n;
  };
  let pending;
  const refresh = () => {
    clearTimeout(pending);
    pending = setTimeout(() => fetch('/cart.js').then((r) => r.json()).then((c) => show(c.item_count)).catch(() => {}), 300);
  };
  document.addEventListener('shopify:cart:lines-update', (e) => Promise.resolve(e.promise).then(refresh, refresh));
  window.addEventListener('focus', refresh);
  window.addEventListener('message', (e) => /shopflo/.test(e.origin) && refresh());
  document.addEventListener('visibilitychange', () => !document.hidden && refresh());
}

/* ---------- helmet finder quiz (snippets/tv-quiz-modal) ---------- */

function initQuiz(root) {
  if (root.dataset.tvReady) return;
  root.dataset.tvReady = '1';
  const data = JSON.parse(root.querySelector('[data-tv-quiz-data]').textContent);
  const $ = (sel) => root.querySelector(sel);
  const fmt = (c) => '₹' + Math.round(c / 100).toLocaleString('en-IN');
  const answers = {};
  let history = [];

  // Women's sizes from the size guide: Small = S/M (560 mm), Medium = M/L (570 mm)
  const STEPS = {
    who: { title: 'Who is this helmet for?', sub: 'We will match the fit and the look.', options: [
      { v: 'me', label: 'For me', note: "Women's helmets", icon: 'helmet' },
      { v: 'kid', label: 'For my kid', note: 'HYP3 kids helmets', icon: 'heart' },
      { v: 'both', label: 'For both of us', note: 'Family Combo · save 18%', icon: 'spark' },
    ] },
    size: { title: 'How big is your head?', sub: 'Measure around your forehead, 2 cm above the eyebrows.', options: [
      { v: 'Small', label: 'Up to 56 cm', note: 'Small · S/M fit', icon: 'ruler' },
      { v: 'Medium', label: '57 – 58 cm', note: 'Medium · M/L fit', icon: 'ruler' },
      { v: 'unsure', label: "I'm not sure", note: "We'll suggest Medium", icon: 'eye' },
    ] },
    kidsize: { title: "How big is your kid's head?", sub: 'Measure around the forehead, just above the eyebrows.', options: [
      { v: 'XS', label: '48 – 50 cm', note: 'XS', icon: 'ruler' },
      { v: 'S', label: '50 – 52 cm', note: 'S', icon: 'ruler' },
      { v: 'M', label: 'Bigger than 52 cm', note: 'M', icon: 'ruler' },
      { v: 'unsure', label: "I'm not sure", note: "We'll suggest S", icon: 'eye' },
    ] },
    ride: { title: 'How do you ride?', sub: 'Pick the one that sounds most like you.', options: [
      { v: 'commute', label: 'Daily commute', note: 'Light and comfy, all day', icon: 'feather' },
      { v: 'monsoon', label: 'Rain or shine', note: 'Rain-proof designs', icon: 'drop' },
      { v: 'college', label: 'College & city', note: 'Prints that turn heads', icon: 'spark' },
      { v: 'highway', label: 'Long & highway rides', note: 'Full-face protection', icon: 'shield' },
    ] },
    style: { title: 'Which style is you?', sub: 'No wrong answers.', options: [
      { v: 'floral', label: 'Bold florals', note: 'Flora · Blossom', icon: 'leaf' },
      { v: 'pastel', label: 'Soft pastels', note: 'Lilac · Aqua · Blush', icon: 'heart' },
      { v: 'classic', label: 'Classic & minimal', note: 'Black · White · Silver', icon: 'helmet' },
      { v: 'shimmer', label: 'Shimmer & shine', note: 'Rose gold · Champagne', icon: 'star' },
    ] },
    kidstyle: { title: 'Which print will they love?', sub: 'Kids actually wear helmets they like.', options: [
      { v: 'Graffiti', label: 'Graffiti', note: 'Street-art colours', icon: 'spark' },
      { v: 'Portal', label: 'Portal', note: 'Swirly and bright', icon: 'star' },
      { v: 'any', label: 'Surprise me', note: 'Show me everything', icon: 'gift' },
    ] },
  };
  const flow = () => (answers.who === 'kid' ? ['who', 'kidsize', 'kidstyle'] : answers.who === 'both' ? ['who', 'size', 'style', 'kidsize', 'kidstyle'] : ['who', 'size', 'ride', 'style']);

  const icon = (name) => {
    const tpl = document.querySelector(`[data-tv-icon="${name}"]`);
    return tpl ? tpl.innerHTML : '';
  };

  const show = (key) => {
    const steps = flow();
    const n = steps.indexOf(key);
    const step = STEPS[key];
    $('[data-tv-quiz-step]').textContent = `Step ${n + 1} of ${steps.length}`;
    $('[data-tv-quiz-title]').textContent = step.title;
    $('[data-tv-quiz-sub]').textContent = step.sub;
    $('[data-tv-quiz-bar]').style.width = `${((n + 1) / (steps.length + 1)) * 100}%`;
    $('[data-tv-quiz-back]').hidden = history.length === 0;
    $('[data-tv-quiz-result]').hidden = true;
    const box = $('[data-tv-quiz-options]');
    box.hidden = false;
    box.innerHTML = '';
    step.options.forEach((o) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'tvq__opt' + (answers[key] === o.v ? ' on' : '');
      b.innerHTML = `<span class="tvq__ic">${icon(o.icon)}</span><span><b></b><small></small></span>`;
      b.querySelector('b').textContent = o.label;
      b.querySelector('small').textContent = o.note;
      b.addEventListener('click', () => {
        answers[key] = o.v;
        history.push(key);
        const next = flow()[flow().indexOf(key) + 1];
        next ? show(next) : result();
      });
      box.append(b);
    });
    box.querySelector('button')?.focus({ preventScroll: true });
  };

  const scoreWomen = (p) => {
    const t = `${p.design} ${p.colour} ${p.tags}`.toLowerCase();
    let s = p.rating * 2 + Math.min(p.reviews, 20) * 0.3;
    if (/best seller|bestseller/.test(p.tags)) s += 4;
    const ride = answers.ride, style = answers.style;
    if (ride === 'highway') s += p.full ? 30 : -10;
    else if (p.full) s -= 20;
    if (ride === 'monsoon' && (/rain/.test(t) || /blossom|lilt/.test(t))) s += 14;
    if (ride === 'college' && /flora|daisy|dreamcatcher|blossom/.test(t)) s += 6;
    if (ride === 'commute' && /best seller|bestseller/.test(p.tags)) s += 3;
    if (style === 'floral' && /flora|blossom/.test(t)) s += 12;
    if (style === 'pastel' && /lilac|aqua|blush|frost|coral|pink|lilt|daisy|sky|mint/.test(t)) s += 12;
    if (style === 'classic' && /black|white|pearl|silver|grey|beige|marine|night/.test(t)) s += 12;
    if (style === 'shimmer' && /rose gold|champagne|bronze|lotus|dreamcatcher|glow/.test(t)) s += 12;
    return s;
  };
  const scoreKid = (p) => {
    let s = p.rating * 2 + p.reviews * 0.3;
    if (answers.kidstyle && answers.kidstyle !== 'any' && p.design.toLowerCase().includes(answers.kidstyle.toLowerCase())) s += 12;
    return s;
  };
  const pickSize = (p, want) => {
    // variant titles are "Medium" or "Lilac Blush / Medium" depending on the product's options
    const w = String(want).toLowerCase();
    const v = p.variants.find((x) => x.available && x.title.toLowerCase().split(' / ').includes(w));
    return v || null;
  };

  const want_label = (v, size) => size || v.title;
  const card = (p, size, sizeLabel) => {
    const v = pickSize(p, size);
    const el = document.createElement('article');
    el.className = 'tvq__card';
    const off = p.compare > p.price ? Math.round(((p.compare - p.price) * 100) / p.compare) : 0;
    el.innerHTML = `<a class="tvq__img" href="${p.url}"><img src="${p.img}" alt="" loading="lazy"></a>
      <div class="tvq__info"><a class="tvq__name" href="${p.url}"></a>
      <p class="tvq__meta">${p.reviews ? `★ ${p.rating.toFixed(1)} (${p.reviews})` : 'New'} · ${sizeLabel}</p>
      <p class="tvq__price"><b>${fmt(p.price)}</b>${off ? ` <s>${fmt(p.compare)}</s> <span>${off}% OFF</span>` : ''}</p></div>`;
    el.querySelector('.tvq__name').textContent = `${p.design}${p.colour ? ' · ' + p.colour : ''}`;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tvq__add';
    if (v) {
      btn.textContent = `Add ${want_label(v, size)} to cart`;
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        try {
          await addVariant(btn, v.id);
          btn.textContent = 'Added ✓';
          setTimeout(close, 700);
        } catch (e) {
          btn.textContent = e.message;
          btn.disabled = false;
        }
      });
    } else {
      btn.textContent = 'Choose size';
      btn.addEventListener('click', () => (window.location.href = p.url));
    }
    el.querySelector('.tvq__info').append(btn);
    return el;
  };

  const result = () => {
    $('[data-tv-quiz-back]').hidden = false;
    $('[data-tv-quiz-bar]').style.width = '100%';
    $('[data-tv-quiz-step]').textContent = 'Your matches';
    $('[data-tv-quiz-options]').hidden = true;
    const out = $('[data-tv-quiz-result]');
    out.hidden = false;
    out.innerHTML = '';
    const list = document.createElement('div');
    list.className = 'tvq__cards';
    const womenSize = answers.size === 'unsure' ? 'Medium' : answers.size;
    const kidSize = answers.kidsize === 'unsure' ? 'S' : answers.kidsize;
    let title = 'Your perfect helmets';
    let sub = '';
    if (answers.who !== 'kid') {
      const picks = data.women.filter((p) => p.variants.some((v) => v.available)).sort((a, b) => scoreWomen(b) - scoreWomen(a)).slice(0, answers.who === 'both' ? 2 : 3);
      picks.forEach((p) => list.append(card(p, womenSize, `Size ${womenSize}`)));
      sub = answers.size === 'unsure'
        ? "Not sure of your size? Medium (M/L) fits most riders between sizes. Or message us a selfie on WhatsApp."
        : `${womenSize} (${womenSize === 'Small' ? 'S/M · 560 mm' : 'M/L · 570 mm'}) suits your head size.`;
    }
    if (answers.who !== 'me') {
      const kids = data.kids.filter((p) => p.variants.some((v) => v.available)).sort((a, b) => scoreKid(b) - scoreKid(a)).slice(0, answers.who === 'kid' ? 3 : 1);
      kids.forEach((p) => list.append(card(p, kidSize, `Kids size ${kidSize}`)));
      if (answers.who === 'kid') { title = 'Helmets they will love'; sub = `Size ${kidSize} fits a ${({ XS: '48–50', S: '50–52', M: '52+' })[kidSize] || ''} cm head.`; }
    }
    if (answers.who === 'both' && data.family) {
      title = 'Ride together';
      sub = 'Pick one for you and one for your kid, or get both in the Family Combo and save.';
      const fam = document.createElement('a');
      fam.className = 'tvq__family';
      fam.href = data.family.url;
      fam.innerHTML = `<img src="${data.family.img}" alt="" loading="lazy"><span><b>Family Combo</b><small>One women's + one HYP3 helmet · from ${fmt(data.family.price)}</small></span><span class="tvq__go">Build it →</span>`;
      out.append(fam);
    }
    $('[data-tv-quiz-title]').textContent = title;
    $('[data-tv-quiz-sub]').textContent = sub;
    out.prepend(list);
    const help = document.createElement('p');
    help.className = 'tvq__help';
    help.innerHTML = `Still unsure? <a href="https://wa.me/${data.whatsapp}?text=${encodeURIComponent('Hi! I took the helmet quiz and need help choosing.')}" target="_blank" rel="noopener">Ask a real rider on WhatsApp</a> · <button type="button" data-tv-quiz-restart>Start again</button>`;
    help.querySelector('[data-tv-quiz-restart]').addEventListener('click', () => start());
    out.append(help);
  };

  const start = (who) => {
    Object.keys(answers).forEach((k) => delete answers[k]);
    history = [];
    if (who) { answers.who = who; history.push('who'); show(flow()[1]); }
    else show('who');
  };
  let lastFocus = null;
  const open = (who) => {
    lastFocus = document.activeElement;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    void root.offsetWidth; // apply the hidden->visible change before starting the fade-in
    root.classList.add('is-open');
    document.documentElement.classList.add('tv-locked');
    start(who);
  };
  const close = () => {
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('tv-locked');
    setTimeout(() => (root.hidden = true), 300);
    lastFocus?.focus?.();
    if (/^#quiz/.test(location.hash)) history_replace();
  };
  const history_replace = () => window.history.replaceState(null, '', location.pathname + location.search);

  $('[data-tv-quiz-back]').addEventListener('click', () => {
    const prev = history.pop();
    if (prev) show(prev);
  });
  root.querySelectorAll('[data-tv-quiz-close]').forEach((b) => b.addEventListener('click', close));
  document.addEventListener('keydown', (e) => e.key === 'Escape' && !root.hidden && close());
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href$="#quiz"], a[href$="#quiz-kids"], [data-tv-quiz-open]');
    if (!a) return;
    e.preventDefault();
    open(/quiz-kids/.test(a.getAttribute('href') || '') ? 'kid' : a.dataset.tvQuizOpen || undefined);
  });
  if (/^#quiz(-kids)?$/.test(location.hash)) open(location.hash === '#quiz-kids' ? 'kid' : undefined);
}

/* ---------- boot (also re-runs inside the theme editor when sections reload) ---------- */

/* limited time deal countdowns (cards + product page): time left until midnight */
let dealClockTimer;
function dealHms() {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const sec = Math.max(0, Math.floor((end - Date.now()) / 1000));
  const [h, m, sx] = [sec / 3600, (sec % 3600) / 60, sec % 60].map((n) => String(Math.floor(n)).padStart(2, '0'));
  return `${h}h ${m}m ${sx}s`;
}

function initDealClocks() {
  if (dealClockTimer) return;
  const tick = () => {
    const els = document.querySelectorAll('[data-tv-dealclock]');
    if (!els.length) return;
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const sec = Math.max(0, Math.floor((end - Date.now()) / 1000));
    const t = [sec / 3600, (sec % 3600) / 60, sec % 60].map((n) => String(Math.floor(n)).padStart(2, '0')).join(':');
    const [h, m, sx] = t.split(':');
    document.querySelectorAll('[data-tv-dealbar]').forEach((b) => { b.style.width = `${Math.max(2, (sec / 86400) * 100).toFixed(2)}%`; });
    els.forEach((el) => {
      if (el.dataset.format === 'hms') el.textContent = `${h}h ${m}m ${sx}s`;
      else if (el.dataset.format === 'boxes') {
        el.querySelector('[data-h]').textContent = h;
        el.querySelector('[data-m]').textContent = m;
        el.querySelector('[data-s]').textContent = sx;
      } else el.textContent = t;
    });
  };
  tick();
  dealClockTimer = setInterval(tick, 1000);
}

/* product cards: rotate the small offer line (free shipping + freebies / Snapmint) */
let cardOfferTimer;
function initCardOffers() {
  if (cardOfferTimer) return;
  let i = 0;
  cardOfferTimer = setInterval(() => {
    i += 1;
    document.querySelectorAll('[data-tv-cardoffer]').forEach((el) => {
      const lines = el.querySelectorAll('.pcard__offer-txt');
      if (lines.length < 2) return;
      lines.forEach((l, n) => { l.hidden = n !== i % lines.length; });
    });
  }, 3200);
}

/* ---------- PDP: worry-answer chips ---------- */
function initFaqRow(root) {
  if (root.dataset.tvReady) return;
  root.dataset.tvReady = '1';
  const chips = [...root.querySelectorAll('[data-faq]')];
  chips.forEach((c) => c.addEventListener('click', () => {
    const open = c.getAttribute('aria-expanded') !== 'true';
    chips.forEach((x) => x.setAttribute('aria-expanded', 'false'));
    root.querySelectorAll('[data-faq-ans]').forEach((a) => { a.hidden = true; });
    if (open) {
      c.setAttribute('aria-expanded', 'true');
      root.querySelector(`[data-faq-ans="${c.dataset.faq}"]`).hidden = false;
    }
  }));
}

/* ---------- PDP: customer quotes under Add to cart (Judge.me) ---------- */
function initQuotes(root) {
  if (root.dataset.tvReady) return;
  root.dataset.tvReady = '1';
  const listEl = root.querySelector('[data-tv-pq-list]');
  const good = (r) => r.rating >= 4 && r.body.length >= 20 && r.body.length <= 220;
  const render = (reviews) => {
    const picks = reviews.filter(good).sort((a, b) => (b.pics.length > 0) - (a.pics.length > 0) || b.rating - a.rating).slice(0, 3);
    if (picks.length < 2) return;
    picks.forEach((r) => {
      const el = document.createElement('figure');
      el.className = 'pq__item';
      const av = r.pics[0]
        ? `<img class="pq__av" src="${r.pics[0].thumb.replace(/width=\d+/, 'width=120')}" alt="" loading="lazy">`
        : `<span class="pq__av pq__av--i">${(r.name || '?').trim().charAt(0).toUpperCase()}</span>`;
      el.innerHTML = `${av}<div><span class="pq__stars">${'★'.repeat(Math.round(r.rating))}</span><blockquote></blockquote><figcaption></figcaption></div>`;
      el.querySelector('blockquote').textContent = r.body;
      el.querySelector('figcaption').textContent = `${r.name}${r.verified ? ' · Verified buyer' : ''}`;
      listEl.append(el);
    });
    root.hidden = false;
  };
  root.querySelector('[data-tv-pq-all]')?.addEventListener('click', (e) => {
    const target = document.querySelector('[data-tv-reviews]') || document.getElementById('reviews');
    if (target) { e.preventDefault(); target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
  });
  const run = async () => {
    try {
      let reviews = await fetchJudgeme(root.dataset.shop, root.dataset.product);
      if (reviews.filter(good).length < 2) {
        const sib = (document.querySelector('[data-tv-reviews]')?.dataset.siblings || '').split('||').filter(Boolean).map((x) => x.split('::')[0]).slice(0, 4);
        const more = await Promise.allSettled(sib.map((id) => fetchJudgeme(root.dataset.shop, id)));
        reviews = reviews.concat(more.flatMap((m) => (m.status === 'fulfilled' ? m.value : [])));
      }
      render(reviews);
    } catch (e) { /* no reviews: keep hidden */ }
  };
  setTimeout(() => ('requestIdleCallback' in window ? requestIdleCallback(run, { timeout: 2500 }) : run()), 1200);
}

/* ---------- Shop-by-category page: tabs that switch the product grid (?tab=<handle>) ---------- */
function initCatTabs(root) {
  if (root.dataset.tvReady) return;
  root.dataset.tvReady = '1';
  const tabs = [...root.querySelectorAll('[role="tab"]')];
  const bar = root.querySelector('.ctab__bar');
  const load = async (panel) => {
    if (panel.dataset.loaded) return;
    panel.dataset.loaded = '1';
    try {
      const html = await (await fetch(panel.dataset.src)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const grid = doc.querySelector('.tv');
      panel.replaceChildren(...(grid ? grid.childNodes : []));
      boot(panel);
    } catch {
      delete panel.dataset.loaded;
    }
  };
  const open = (tab, scroll) => {
    tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', on);
      document.getElementById(t.getAttribute('aria-controls')).hidden = !on;
    });
    tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: scroll ? 'smooth' : 'auto' });
    load(document.getElementById(tab.getAttribute('aria-controls')));
    const url = new URL(location.href);
    url.searchParams.set('tab', tab.dataset.handle);
    history.replaceState(null, '', url);
    if (scroll && bar.getBoundingClientRect().top < 1) window.scrollTo({ top: root.querySelector('.ctab__panels').getBoundingClientRect().top + scrollY - bar.offsetHeight - 70, behavior: 'smooth' });
  };
  tabs.forEach((t) => t.addEventListener('click', () => open(t, true)));
  const want = new URLSearchParams(location.search).get('tab');
  const start = tabs.find((t) => t.dataset.handle === want);
  if (start && start !== tabs[0]) open(start, false);
  /* warm the next tabs once the page is idle */
  const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1500));
  idle(() => tabs.slice(1, 4).forEach((t) => load(document.getElementById(t.getAttribute('aria-controls')))), { timeout: 4000 });
}

/* ---------- Homepage video banner: load only the fitting video, after the page has loaded ---------- */
function initHeroVideo(root) {
  if (root.dataset.tvReady) return;
  root.dataset.tvReady = '1';
  const conn = navigator.connection;
  if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const start = () => {
    const v = document.createElement('video');
    v.className = 'hero4__video';
    v.muted = true;
    v.loop = true;
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('aria-hidden', 'true');
    v.preload = 'auto';
    v.src = matchMedia('(min-width: 750px)').matches ? root.dataset.d : root.dataset.m;
    v.addEventListener('playing', () => root.classList.add('is-playing'), { once: true });
    root.append(v);
    v.play().catch(() => {});
  };
  const later = () => (window.requestIdleCallback ? requestIdleCallback(start, { timeout: 2000 }) : setTimeout(start, 300));
  if (document.readyState === 'complete') later();
  else addEventListener('load', later, { once: true });
}

/* ---------- Easy Bundle Builder products: add the bundle by fetch, not a page submit ----------
   The app swaps the ₹0 bundle line for the chosen items only on a fetch to /cart/add. A plain form
   submit adds the empty bundle and lands on Shopify's /cart page; by fetch, the tv-head hook opens Shopflo. */
function initBundleForm(form) {
  if (form.dataset.tvReady) return;
  form.dataset.tvReady = '1';
  let busy = false;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (busy) return;
    busy = true;
    const btn = form.querySelector('[type="submit"]');
    if (btn) btn.setAttribute('aria-busy', 'true');
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(d.description || d.message || 'Could not add the bundle. Please try again.');
      }
    } catch (err) {
      alert('Could not add the bundle. Please check your connection and try again.');
    } finally {
      busy = false;
      if (btn) btn.removeAttribute('aria-busy');
    }
  });
}

function boot(scope = document) {
  scope.querySelectorAll('[data-tv-atc]').forEach(initAtc);
  scope.querySelectorAll('[data-tv-card]').forEach(initGallery);
  scope.querySelectorAll('.tv .rail').forEach(initRail);
  scope.querySelectorAll('[data-tv-tabs]').forEach(initTabs);
  scope.querySelectorAll('[data-tv-reels]').forEach(initReels);
  scope.querySelectorAll('[data-tv-related]').forEach(initRelated);
  scope.querySelectorAll('[data-tv-pdp]').forEach(initPdp);
  scope.querySelectorAll('[data-tv-collection]').forEach(initCollection);
  scope.querySelectorAll('[data-tv-reviews]').forEach(initReviews);
  scope.querySelectorAll('[data-tv-strip]').forEach(initStrip);
  scope.querySelectorAll('[data-tv-header]').forEach(initHeader);
  scope.querySelectorAll('[data-tv-quiz]').forEach(initQuiz);
  initCardOffers();
  initDealClocks();
  scope.querySelectorAll('[data-tv-faqrow]').forEach(initFaqRow);
  scope.querySelectorAll('[data-tv-pq]').forEach(initQuotes);
  scope.querySelectorAll('[data-tv-catabs]').forEach(initCatTabs);
  scope.querySelectorAll('[data-tv-herovid]').forEach(initHeroVideo);
  scope.querySelectorAll('.bb__ebb form[action*="/cart/add"]').forEach(initBundleForm);
}

boot();
document.addEventListener('shopify:section:load', (e) => boot(e.target));

